import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, PageHeader, Pill, KPI, brl, dt } from "./_shared";
import { Save, X, TrendingUp, Wallet, Package2, Layers, BarChart2, History, Ticket, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------
const money = (v = 0) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
const percent = (n = 0) => new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 1 }).format(n || 0);

const FAMILY_LABELS = {
  ASSINATURA: "Assinatura Digital",
  OCR: "OCR",
  PROCESSOS: "Processos",
  DJEN: "DJEN",
  ARENA: "Arena OAB",
  CREDITO: "Hermes Crédito",
  INTELIGENCIA_360: "Inteligência 360",
  MINERAL: "Hermes Mineral",
  IA: "Hermes IA",
  PECAS: "Peças",
  CALCULOS: "Cálculos",
};

const TABS = [
  { key: "dashboard", label: "KPIs", icon: BarChart2 },
  { key: "plans", label: "Planos", icon: Package2 },
  { key: "services", label: "Serviços", icon: Layers },
  { key: "entitlements", label: "Franquias", icon: TrendingUp },
  { key: "wallets", label: "Carteiras", icon: Wallet },
  { key: "coupons", label: "Cupons", icon: Ticket },
];

// -----------------------------------------------------------------------------
// Root
// -----------------------------------------------------------------------------
export default function AdminComercial() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="p-3 sm:p-6 lg:p-8" data-testid="admin-comercial">
      <PageHeader
        title="Comercial"
        subtitle="Motor de planos, serviços, franquias e carteiras Hermes"
      />
      <nav className="flex gap-1 mb-6 border-b border-white/5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`comercial-tab-${t.key}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              tab === t.key
                ? "border-[#d4a24c] text-[#d4a24c]"
                : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </nav>

      {tab === "dashboard" && <DashboardTab />}
      {tab === "plans" && <PlansTab />}
      {tab === "services" && <ServicesTab />}
      {tab === "entitlements" && <EntitlementsTab />}
      {tab === "wallets" && <WalletsTab />}
      {tab === "coupons" && <CouponsTab />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// DASHBOARD TAB
// -----------------------------------------------------------------------------
function DashboardTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/billing/admin/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  }, []);
  if (!data) return <div className="text-slate-500">Carregando…</div>;
  const subs = data.subscribers_by_plan || {};
  return (
    <div className="space-y-6" data-testid="comercial-dashboard-tab">
      {/* Recorrente */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI label="MRR (Recorrente Mensal)" value={money(data.mrr)} tone="gold" hint={`${data.active_subscribers || 0} assinantes ativos`} testid="kpi-mrr" />
        <KPI label="ARPU" value={money(data.arpu)} tone="gold" hint="Receita média por conta" testid="kpi-arpu" />
        <KPI label="Churn 30 dias" value={`${data.churn_30d_pct || 0}%`} tone={data.churn_30d_pct > 5 ? "red" : "green"} hint={`${data.cancelled_30d || 0} cancelamentos`} testid="kpi-churn" />
        <KPI label="Assinantes ativos" value={data.active_subscribers || 0} tone="blue" testid="kpi-active-subs" />
      </div>
      {/* Carteira */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <KPI label="Carteiras" value={data.wallets?.count ?? 0} hint={`Ciclo ${data.period_key}`} testid="kpi-wallets" />
        <KPI label="Saldo consolidado" value={money(data.wallets?.total_balance)} tone="gold" testid="kpi-balance" />
        <KPI label="Créditos no ciclo" value={money(data.credits_cycle?.total)} hint={`${data.credits_cycle?.count || 0} entradas`} tone="green" testid="kpi-credits" />
        <KPI label="Débitos no ciclo" value={money(data.debits_cycle?.total)} hint={`${data.debits_cycle?.count || 0} saídas`} tone="red" testid="kpi-debits" />
      </div>

      <Card title="Serviços mais consumidos (ciclo atual)" testid="comercial-top-services">
        {(data.top_services || []).length === 0 ? (
          <div className="text-sm text-slate-500">Nenhum consumo registrado neste ciclo.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left py-2">Serviço</th>
                <th className="text-right py-2">Unidades committadas</th>
              </tr>
            </thead>
            <tbody>
              {data.top_services.map((s) => (
                <tr key={s._id} className="border-t border-white/5">
                  <td className="py-2 text-slate-200">{s._id}</td>
                  <td className="py-2 text-right text-[#d4a24c] font-semibold">{s.consumed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Assinantes por plano" testid="comercial-subs">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {["estagiario", "junior", "pleno", "senior", "master"].map((p) => (
            <div key={p} className="rounded-lg border border-white/5 bg-[#0a0d17] p-4 text-center">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{p}</div>
              <div className="text-2xl font-serif text-[#d4a24c] mt-1">{subs[p] || 0}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PLANS TAB
// -----------------------------------------------------------------------------
function PlansTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const load = useCallback(() => {
    api.get("/billing/admin/plans").then((r) => setItems(r.data.items));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (code) => {
    const patch = editing[code];
    if (!patch) return;
    const body = { ...patch };
    if (body.price_monthly !== undefined) {
      body.price_monthly = parseFloat(String(body.price_monthly).replace(",", "."));
    }
    await api.patch(`/billing/admin/plans/${code}`, body);
    setEditing((s) => { const c = { ...s }; delete c[code]; return c; });
    load();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="comercial-plans-tab">
      {items.map((p) => {
        const e = editing[p.code];
        return (
          <Card key={p.code} kicker={`Ordem ${p.order}`} title={p.name}
            right={<Pill tone={p.active ? "green" : "slate"}>{p.active ? "ativo" : "inativo"}</Pill>}
            testid={`plan-card-${p.code}`}>
            {!e ? (
              <>
                <div className="text-3xl font-serif text-[#d4a24c]">
                  {money(p.price_monthly)}<span className="text-xs text-slate-500">/mês</span>
                </div>
                <div className="text-xs text-slate-500 mt-1">code: <code className="text-slate-300">{p.code}</code></div>
                <div className="mt-3 space-y-1 text-xs text-slate-400">
                  <div>Arena OAB inclusa: <span className={p.arena_included ? "text-emerald-400" : "text-slate-500"}>{p.arena_included ? "sim" : "não"}</span></div>
                  <div>Destaque no paywall: <span className={p.highlight ? "text-[#d4a24c]" : "text-slate-500"}>{p.highlight ? "sim" : "não"}</span></div>
                  <div>Assinantes ativos: <span className="text-slate-200 font-semibold">{p.subscribers ?? 0}</span></div>
                </div>
                <button className="mt-4 px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs"
                  data-testid={`plan-edit-${p.code}`}
                  onClick={() => setEditing({ ...editing, [p.code]: {
                    name: p.name, price_monthly: p.price_monthly,
                    arena_included: !!p.arena_included, highlight: !!p.highlight, active: !!p.active,
                  }})}>Editar</button>
              </>
            ) : (
              <div className="space-y-2">
                <input className="w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                  value={e.name} onChange={(ev) => setEditing({ ...editing, [p.code]: { ...e, name: ev.target.value } })}
                  data-testid={`plan-input-name-${p.code}`}/>
                <input className="w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
                  placeholder="Preço mensal" value={e.price_monthly}
                  onChange={(ev) => setEditing({ ...editing, [p.code]: { ...e, price_monthly: ev.target.value } })}
                  data-testid={`plan-input-price-${p.code}`}/>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={e.arena_included}
                    onChange={(ev) => setEditing({ ...editing, [p.code]: { ...e, arena_included: ev.target.checked } })}/>
                  Arena OAB inclusa
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={e.highlight}
                    onChange={(ev) => setEditing({ ...editing, [p.code]: { ...e, highlight: ev.target.checked } })}/>
                  Destaque no paywall
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={e.active}
                    onChange={(ev) => setEditing({ ...editing, [p.code]: { ...e, active: ev.target.checked } })}/>
                  Ativo
                </label>
                <div className="flex gap-2">
                  <button onClick={() => save(p.code)}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600/80 text-white text-xs flex items-center gap-1"
                    data-testid={`plan-save-${p.code}`}><Save className="w-3 h-3"/>Salvar</button>
                  <button onClick={() => setEditing((s) => { const c = { ...s }; delete c[p.code]; return c; })}
                    className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs flex items-center gap-1"
                    data-testid={`plan-cancel-${p.code}`}><X className="w-3 h-3"/>Cancelar</button>
                </div>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// SERVICES TAB
// -----------------------------------------------------------------------------
function ServicesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState({});
  const [historyFor, setHistoryFor] = useState(null);
  const load = useCallback(() => {
    api.get("/billing/admin/services").then((r) => setItems(r.data.items));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (code) => {
    const patch = editing[code];
    if (!patch) return;
    const body = { ...patch };
    ["public_price", "overage_price"].forEach((k) => {
      if (body[k] !== undefined) body[k] = parseFloat(String(body[k]).replace(",", "."));
    });
    if (!body.reason) {
      alert("Motivo é obrigatório ao alterar preço.");
      return;
    }
    try {
      await api.patch(`/billing/admin/services/${code}`, body);
      setEditing((s) => { const c = { ...s }; delete c[code]; return c; });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const grouped = items.reduce((acc, s) => {
    (acc[s.family] = acc[s.family] || []).push(s); return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="comercial-services-tab">
      {Object.entries(grouped).map(([family, list]) => (
        <Card key={family} title={FAMILY_LABELS[family] || family} kicker={`${list.length} serviço(s)`} testid={`family-${family}`}>
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
              <tr className="border-b border-white/5">
                <th className="text-left py-2">Código</th>
                <th className="text-left py-2">Nome</th>
                <th className="text-right py-2">Preço público</th>
                <th className="text-right py-2">Excedente</th>
                <th className="text-center py-2">Status</th>
                <th className="text-center py-2">Versão</th>
                <th className="text-right py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => {
                const e = editing[s.code];
                return (
                  <tr key={s.code} className="border-b border-white/5" data-testid={`service-row-${s.code}`}>
                    <td className="py-2"><code className="text-[#d4a24c] text-xs">{s.code}</code></td>
                    <td className="py-2 text-slate-300">{s.name}</td>
                    <td className="py-2 text-right">
                      {e ? (
                        <input className="w-24 bg-[#0a0d17] border border-white/5 rounded px-2 py-1 text-xs text-right text-slate-200"
                          value={e.public_price}
                          onChange={(ev) => setEditing({ ...editing, [s.code]: { ...e, public_price: ev.target.value } })}/>
                      ) : <span className="text-slate-200">{money(s.public_price)}</span>}
                    </td>
                    <td className="py-2 text-right">
                      {e ? (
                        <input className="w-24 bg-[#0a0d17] border border-white/5 rounded px-2 py-1 text-xs text-right text-slate-200"
                          value={e.overage_price}
                          onChange={(ev) => setEditing({ ...editing, [s.code]: { ...e, overage_price: ev.target.value } })}/>
                      ) : <span className="text-slate-400">{money(s.overage_price)}</span>}
                    </td>
                    <td className="py-2 text-center">
                      <Pill tone={s.status === "ACTIVE" ? "green" : "slate"}>{s.status}</Pill>
                    </td>
                    <td className="py-2 text-center text-slate-500 text-xs">v{s.price_version}</td>
                    <td className="py-2 text-right">
                      {e ? (
                        <div className="flex gap-1 justify-end items-center">
                          <input placeholder="motivo *"
                            className="w-32 bg-[#0a0d17] border border-white/5 rounded px-2 py-1 text-xs text-slate-200"
                            value={e.reason || ""}
                            onChange={(ev) => setEditing({ ...editing, [s.code]: { ...e, reason: ev.target.value } })}/>
                          <button onClick={() => save(s.code)}
                            className="px-2 py-1 rounded bg-emerald-600/80 text-white text-xs"
                            data-testid={`service-save-${s.code}`}><Save className="w-3 h-3"/></button>
                          <button onClick={() => setEditing((st) => { const c = { ...st }; delete c[s.code]; return c; })}
                            className="px-2 py-1 rounded border border-white/10 text-slate-300 text-xs">
                            <X className="w-3 h-3"/>
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 justify-end">
                          <button onClick={() => setEditing({ ...editing, [s.code]: { public_price: s.public_price, overage_price: s.overage_price, reason: "" } })}
                            className="px-3 py-1 rounded border border-white/10 text-slate-300 text-xs"
                            data-testid={`service-edit-${s.code}`}>Editar</button>
                          <button onClick={() => setHistoryFor(s.code)}
                            className="px-2 py-1 rounded border border-white/10 text-slate-400 text-xs flex items-center gap-1"
                            data-testid={`service-history-${s.code}`}><History className="w-3 h-3"/></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      ))}

      {historyFor && <PriceHistoryModal code={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}

function PriceHistoryModal({ code, onClose }) {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    api.get(`/billing/admin/services/${code}/price-history`).then((r) => setVersions(r.data.items));
  }, [code]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}
      data-testid="price-history-modal">
      <div className="bg-[#111726] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Histórico de preços</div>
            <div className="text-slate-100 font-semibold">{code}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="overflow-y-auto p-5">
          {!versions ? <div className="text-slate-500 text-sm">Carregando…</div>
            : versions.length === 0 ? <div className="text-slate-500 text-sm">Sem versões registradas.</div>
            : (
              <table className="w-full text-sm">
                <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
                  <tr><th className="text-left py-2">v</th><th className="text-left py-2">Data</th><th className="text-right py-2">Público</th><th className="text-right py-2">Excedente</th><th className="text-left py-2">Motivo</th></tr>
                </thead>
                <tbody>
                  {versions.map((v) => (
                    <tr key={v.version} className="border-t border-white/5">
                      <td className="py-2 text-[#d4a24c]">v{v.version}</td>
                      <td className="py-2 text-slate-400 text-xs">{dt(v.effective_from)}</td>
                      <td className="py-2 text-right text-slate-200">{money(v.public_price)}</td>
                      <td className="py-2 text-right text-slate-400">{money(v.overage_price)}</td>
                      <td className="py-2 text-slate-400 text-xs">{v.reason || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          }
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ENTITLEMENTS TAB
// -----------------------------------------------------------------------------
function EntitlementsTab() {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState({}); // key `${plan}/${svc}` -> value
  const load = useCallback(() => {
    api.get("/billing/admin/entitlements").then((r) => setData(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const cellKey = (p, s) => `${p}/${s}`;

  const save = async (plan, svc) => {
    const key = cellKey(plan, svc);
    const raw = editing[key];
    if (raw === undefined) return;
    let qty;
    if (String(raw).toLowerCase() === "∞" || String(raw).toLowerCase() === "unlimited") qty = -1;
    else qty = parseInt(raw, 10);
    if (Number.isNaN(qty)) { alert("Valor inválido (use -1 para ilimitado)."); return; }
    await api.patch(`/billing/admin/entitlements/${plan}/${svc}`, { included_quantity: qty, overage_allowed: true });
    setEditing((s) => { const c = { ...s }; delete c[key]; return c; });
    load();
  };

  if (!data) return <div className="text-slate-500">Carregando…</div>;

  return (
    <Card title="Matriz plano × serviço" kicker="Franquias mensais · digite -1 para ilimitado" testid="comercial-entitlements-tab">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-white/5">
              <th className="text-left py-2 sticky left-0 bg-[#111726]/70">Serviço</th>
              {data.plans.map((p) => (
                <th key={p.code} className="text-center py-2 px-3">{p.name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.services.map((s) => (
              <tr key={s.code} className="border-b border-white/5" data-testid={`ent-row-${s.code}`}>
                <td className="py-2 sticky left-0 bg-[#111726]/70">
                  <div className="text-slate-200 text-xs">{s.name}</div>
                  <code className="text-[10px] text-[#d4a24c]">{s.code}</code>
                </td>
                {data.plans.map((p) => {
                  const ent = data.matrix?.[p.code]?.[s.code];
                  const key = cellKey(p.code, s.code);
                  const isEditing = editing[key] !== undefined;
                  const display = ent?.unlimited ? "∞" : (ent?.included_quantity ?? 0);
                  return (
                    <td key={p.code} className="text-center py-2 px-2">
                      {isEditing ? (
                        <div className="flex items-center gap-1 justify-center">
                          <input
                            autoFocus
                            className="w-16 bg-[#0a0d17] border border-white/5 rounded px-2 py-1 text-xs text-center text-slate-200"
                            value={editing[key]}
                            onChange={(ev) => setEditing({ ...editing, [key]: ev.target.value })}
                            onKeyDown={(ev) => { if (ev.key === "Enter") save(p.code, s.code); if (ev.key === "Escape") setEditing((st) => { const c={...st}; delete c[key]; return c; }); }}
                            data-testid={`ent-input-${p.code}-${s.code}`}
                          />
                          <button onClick={() => save(p.code, s.code)}
                            className="text-emerald-400 hover:text-emerald-300"
                            data-testid={`ent-save-${p.code}-${s.code}`}><Save className="w-3 h-3"/></button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditing({ ...editing, [key]: display === "∞" ? "-1" : String(display) })}
                          className={`w-14 py-1 rounded text-sm ${
                            display === "∞" ? "bg-emerald-900/40 text-emerald-300"
                              : display === 0 ? "text-slate-600 hover:bg-white/5"
                              : "text-[#d4a24c] hover:bg-white/5"
                          }`}
                          data-testid={`ent-cell-${p.code}-${s.code}`}
                        >
                          {display}
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 text-xs text-slate-500">
        Clique na célula para editar. <span className="text-slate-300">∞</span> = ilimitado (use -1).
        Serviços com <span className="text-[#d4a24c]">preço</span> aplicam excedente automaticamente após a franquia.
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// WALLETS TAB
// -----------------------------------------------------------------------------
function WalletsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [adjust, setAdjust] = useState({ open: false, account_id: "", amount: "", reason: "", entry_type: "ADMIN_ADJUSTMENT" });

  const load = useCallback(() => {
    api.get("/billing/admin/wallets", { params: q ? { q } : {} })
      .then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const openAdjust = (account_id) => setAdjust({ open: true, account_id, amount: "", reason: "", entry_type: "ADMIN_ADJUSTMENT" });
  const submitAdjust = async () => {
    const amount = parseFloat(String(adjust.amount).replace(",", "."));
    if (Number.isNaN(amount) || amount === 0) { alert("Informe um valor diferente de zero (use negativo para débito)"); return; }
    if (!adjust.reason.trim()) { alert("Motivo obrigatório"); return; }
    await api.post("/billing/admin/wallet/adjust", {
      account_id: adjust.account_id, amount, reason: adjust.reason, entry_type: adjust.entry_type,
    });
    setAdjust({ open: false, account_id: "", amount: "", reason: "", entry_type: "ADMIN_ADJUSTMENT" });
    load();
  };

  return (
    <div className="space-y-4" data-testid="comercial-wallets-tab">
      <div className="flex items-center gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por account_id"
          className="flex-1 bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"
          data-testid="wallets-search"/>
        <span className="text-xs text-slate-500">{total} carteira(s)</span>
      </div>

      <Card testid="wallets-table" title="Carteiras Hermes">
        {items.length === 0 ? <div className="text-slate-500 text-sm">Nenhuma carteira encontrada.</div> : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-white/5">
              <tr>
                <th className="text-left py-2">Usuário</th>
                <th className="text-left py-2">Account ID</th>
                <th className="text-right py-2">Saldo</th>
                <th className="text-right py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.account_id} className="border-b border-white/5">
                  <td className="py-2 text-slate-300">
                    <div>{w.user_name || "—"}</div>
                    <div className="text-xs text-slate-500">{w.user_email}</div>
                  </td>
                  <td className="py-2"><code className="text-[11px] text-slate-400">{w.account_id}</code></td>
                  <td className="py-2 text-right text-[#d4a24c] font-semibold">{money(w.balance)}</td>
                  <td className="py-2 text-right">
                    <button onClick={() => setDrawer(w.account_id)}
                      className="px-2 py-1 rounded border border-white/10 text-slate-300 text-xs mr-2"
                      data-testid={`wallet-view-${w.account_id}`}>Extrato</button>
                    <button onClick={() => openAdjust(w.account_id)}
                      className="px-2 py-1 rounded bg-[#d4a24c] text-black text-xs"
                      data-testid={`wallet-adjust-${w.account_id}`}>Ajustar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {drawer && <WalletDrawer account_id={drawer} onClose={() => setDrawer(null)} />}
      {adjust.open && (
        <AdjustModal state={adjust} setState={setAdjust} onSubmit={submitAdjust} />
      )}
    </div>
  );
}

function WalletDrawer({ account_id, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/billing/admin/wallet/${account_id}`, { params: { limit: 100 } }).then((r) => setData(r.data));
  }, [account_id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex justify-end" onClick={onClose} data-testid="wallet-drawer">
      <div className="w-full max-w-xl bg-[#111726] border-l border-white/10 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Carteira</div>
            <code className="text-[#d4a24c] text-xs">{account_id}</code>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5">
          {!data ? <div className="text-slate-500 text-sm">Carregando…</div> : (
            <>
              <div className="text-4xl font-serif text-[#d4a24c] mb-6">{money(data.balance)}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Extrato</div>
              {data.history.length === 0 ? <div className="text-slate-500 text-sm">Sem lançamentos.</div> : (
                <div className="space-y-2">
                  {data.history.map((e) => (
                    <div key={e.id} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                      <div>
                        <div className={e.direction === "credit" ? "text-emerald-400" : "text-rose-400"}>{e.type}</div>
                        <div className="text-xs text-slate-500">{dt(e.timestamp)} · {e.reason}</div>
                      </div>
                      <div className={`font-semibold ${e.direction === "credit" ? "text-emerald-400" : "text-rose-400"}`}>
                        {e.direction === "credit" ? "+" : "−"}{money(Math.abs(e.amount))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdjustModal({ state, setState, onSubmit }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => setState({ ...state, open: false })} data-testid="adjust-modal">
      <div className="bg-[#111726] border border-white/10 rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Ajuste manual</div>
        <div className="text-slate-100 font-semibold mb-4"><code className="text-xs text-[#d4a24c]">{state.account_id}</code></div>
        <label className="block text-xs text-slate-400 mb-1">Tipo</label>
        <select value={state.entry_type} onChange={(e) => setState({ ...state, entry_type: e.target.value })}
          className="w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200 mb-3"
          data-testid="adjust-type">
          <option value="ADMIN_ADJUSTMENT">Ajuste administrativo</option>
          <option value="PROMOTIONAL_CREDIT">Crédito promocional</option>
          <option value="REFUND">Reembolso</option>
          <option value="REVERSAL">Estorno</option>
        </select>
        <label className="block text-xs text-slate-400 mb-1">Valor (negativo para débito)</label>
        <input value={state.amount} onChange={(e) => setState({ ...state, amount: e.target.value })}
          placeholder="10,00" data-testid="adjust-amount"
          className="w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200 mb-3"/>
        <label className="block text-xs text-slate-400 mb-1">Motivo *</label>
        <input value={state.reason} onChange={(e) => setState({ ...state, reason: e.target.value })}
          placeholder="obrigatório para trilha de auditoria" data-testid="adjust-reason"
          className="w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200 mb-4"/>
        <div className="flex gap-2 justify-end">
          <button onClick={() => setState({ ...state, open: false })}
            className="px-3 py-1.5 rounded-lg border border-white/10 text-slate-300 text-xs">Cancelar</button>
          <button onClick={onSubmit} data-testid="adjust-submit"
            className="px-3 py-1.5 rounded-lg bg-emerald-600/80 text-white text-xs flex items-center gap-1">
            <Save className="w-3 h-3"/> Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// COUPONS TAB (Fase 9 — motor novo /billing/admin/coupons)
// -----------------------------------------------------------------------------
function CouponsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [redemptionsFor, setRedemptionsFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/billing/admin/coupons");
      setRows(r.data.items || []);
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao carregar cupons");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    const nextStatus = c.status === "active" ? "paused" : "active";
    try {
      await api.patch(`/billing/admin/coupons/${c.code}`, { status: nextStatus });
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Falha ao atualizar"); }
  };

  const archive = async (c) => {
    if (!window.confirm(`Arquivar cupom ${c.code}? Ele não poderá mais ser resgatado.`)) return;
    try {
      await api.delete(`/billing/admin/coupons/${c.code}`);
      load();
    } catch (e) { alert(e?.response?.data?.detail || "Falha ao arquivar"); }
  };

  return (
    <div className="space-y-6" data-testid="comercial-coupons-tab">
      <Card title="Cupons Hermes" kicker="Motor comercial (Fase 9)" testid="coupons-list"
        right={
          <button onClick={() => setCreating(true)} data-testid="coupons-new-btn"
            className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold inline-flex items-center gap-2">
            <Plus className="w-4 h-4"/> Novo cupom
          </button>
        }>
        {loading ? (
          <div className="text-slate-500 py-6 text-center text-sm">Carregando…</div>
        ) : rows.length === 0 ? (
          <div className="text-slate-500 py-8 text-center text-sm" data-testid="coupons-empty">
            Nenhum cupom criado. Clique em <b>Novo cupom</b> para começar.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
              <tr className="border-b border-white/5">
                <th className="text-left py-2">Código</th>
                <th className="text-left py-2">Tipo</th>
                <th className="text-right py-2">Valor</th>
                <th className="text-center py-2">Aplica em</th>
                <th className="text-center py-2">Usos</th>
                <th className="text-left py-2">Válido até</th>
                <th className="text-center py-2">Status</th>
                <th className="text-right py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.code} className="border-b border-white/5" data-testid={`coupon-row-${c.code}`}>
                  <td className="py-2 font-mono text-[#d4a24c] font-semibold">{c.code}</td>
                  <td className="py-2 text-slate-300">
                    {c.kind === "percentage" ? "Percentual" : "Valor fixo"}
                  </td>
                  <td className="py-2 text-right text-slate-200">
                    {c.kind === "percentage" ? `${c.value}%` : money(c.value)}
                  </td>
                  <td className="py-2 text-center text-slate-400 text-xs">
                    {c.applies_to === "subscription" && <Pill tone="gold">Assinaturas</Pill>}
                    {c.applies_to === "wallet_topup" && <Pill tone="green">Carteira</Pill>}
                    {c.applies_to === "any" && <Pill tone="slate">Qualquer</Pill>}
                  </td>
                  <td className="py-2 text-center text-slate-300">
                    {c.uses ?? 0}{c.max_uses ? ` / ${c.max_uses}` : ""}
                  </td>
                  <td className="py-2 text-slate-400 text-xs">
                    {c.valid_until ? dt(c.valid_until) : "—"}
                  </td>
                  <td className="py-2 text-center">
                    <Pill tone={c.status === "active" ? "green" : c.status === "archived" ? "red" : "slate"}>
                      {c.status}
                    </Pill>
                  </td>
                  <td className="py-2 text-right">
                    <div className="flex gap-1 justify-end items-center">
                      <button onClick={() => setRedemptionsFor(c.code)}
                        title="Ver resgates"
                        className="p-1.5 rounded border border-white/10 text-slate-400 hover:text-slate-200"
                        data-testid={`coupon-redemptions-${c.code}`}>
                        <History className="w-3 h-3"/>
                      </button>
                      {c.status !== "archived" && (
                        <button onClick={() => toggle(c)}
                          title={c.status === "active" ? "Pausar" : "Ativar"}
                          className="p-1.5 rounded border border-white/10 text-slate-400 hover:text-slate-200"
                          data-testid={`coupon-toggle-${c.code}`}>
                          {c.status === "active"
                            ? <ToggleRight className="w-3.5 h-3.5 text-emerald-400"/>
                            : <ToggleLeft className="w-3.5 h-3.5"/>}
                        </button>
                      )}
                      {c.status !== "archived" && (
                        <button onClick={() => archive(c)}
                          title="Arquivar"
                          className="p-1.5 rounded border border-white/10 text-rose-400 hover:bg-rose-900/20"
                          data-testid={`coupon-archive-${c.code}`}>
                          <Trash2 className="w-3 h-3"/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {creating && <CouponFormModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {redemptionsFor && <CouponRedemptionsModal code={redemptionsFor} onClose={() => setRedemptionsFor(null)} />}
    </div>
  );
}

function CouponFormModal({ onClose, onSaved }) {
  const [f, setF] = useState({
    code: "", name: "", kind: "percentage", value: 10,
    applies_to: "any", max_uses: "", max_uses_per_user: 1, valid_until: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!f.code.trim() || f.code.trim().length < 3) return alert("Código deve ter ao menos 3 caracteres");
    const val = parseFloat(String(f.value).replace(",", "."));
    if (!(val > 0)) return alert("Valor deve ser maior que zero");
    if (f.kind === "percentage" && val > 100) return alert("Percentual máximo 100");
    setSaving(true);
    try {
      const payload = {
        code: f.code.trim().toUpperCase(),
        name: f.name || null,
        kind: f.kind,
        value: val,
        applies_to: f.applies_to,
        max_uses: f.max_uses ? parseInt(f.max_uses) : null,
        max_uses_per_user: parseInt(f.max_uses_per_user) || 1,
        valid_until: f.valid_until || null,
      };
      await api.post("/billing/admin/coupons", payload);
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || "Falha ao criar cupom");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}
      data-testid="coupon-form-modal">
      <div className="bg-[#111726] border border-white/10 rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">Novo cupom · Motor comercial</div>
        <h3 className="text-slate-100 font-serif text-xl mt-1 mb-4">Criar cupom Hermes</h3>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Código *</label>
            <input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
              placeholder="HERMES20" data-testid="coupon-form-code"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200 font-mono"/>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Nome (opcional)</label>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              placeholder="Campanha lançamento" data-testid="coupon-form-name"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"/>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Tipo</label>
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}
              data-testid="coupon-form-kind"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200">
              <option value="percentage">Percentual (%)</option>
              <option value="fixed">Valor fixo (R$)</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">
              Valor {f.kind === "percentage" ? "(1–100)" : "(BRL)"}
            </label>
            <input type="number" step="0.01" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })}
              data-testid="coupon-form-value"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"/>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Aplica-se a</label>
            <select value={f.applies_to} onChange={(e) => setF({ ...f, applies_to: e.target.value })}
              data-testid="coupon-form-applies"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200">
              <option value="any">Qualquer contexto</option>
              <option value="subscription">Somente assinaturas</option>
              <option value="wallet_topup">Somente recarga da carteira</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Máx. usos totais</label>
            <input type="number" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })}
              placeholder="Vazio = ilimitado" data-testid="coupon-form-maxuses"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"/>
          </div>
          <div>
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Máx. por usuário</label>
            <input type="number" value={f.max_uses_per_user} onChange={(e) => setF({ ...f, max_uses_per_user: e.target.value })}
              data-testid="coupon-form-maxperuser"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"/>
          </div>
          <div className="col-span-2">
            <label className="text-[11px] uppercase text-slate-400 tracking-widest">Válido até (opcional)</label>
            <input type="date" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })}
              data-testid="coupon-form-validuntil"
              className="mt-1 w-full bg-[#0a0d17] border border-white/5 rounded-lg px-3 py-2 text-sm text-slate-200"/>
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-6">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="coupon-form-submit"
            className="px-4 py-2 rounded-lg bg-[#d4a24c] text-black text-sm font-semibold inline-flex items-center gap-2">
            <Save className="w-4 h-4"/> {saving ? "Salvando…" : "Criar cupom"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CouponRedemptionsModal({ code, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/billing/admin/coupons/${code}/redemptions`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [code]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}
      data-testid="coupon-redemptions-modal">
      <div className="bg-[#111726] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Resgates do cupom</div>
            <div className="text-slate-100 font-semibold font-mono text-[#d4a24c]">{code}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-100"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {!items ? (
            <div className="text-slate-500 text-sm text-center py-6">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="text-slate-500 text-sm text-center py-6">Nenhum resgate registrado ainda.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-slate-500 text-[11px] uppercase tracking-wider">
                <tr className="border-b border-white/5">
                  <th className="text-left py-2">Quando</th>
                  <th className="text-left py-2">Usuário</th>
                  <th className="text-right py-2">Desconto</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="py-2 text-slate-400 text-xs">{dt(r.created_at)}</td>
                    <td className="py-2 text-slate-300 font-mono text-xs">{r.user_id || "—"}</td>
                    <td className="py-2 text-right text-emerald-400">
                      {r.discount_amount != null ? money(r.discount_amount) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
